import { FC } from 'react';
import { Box, Button, InputBase, Tooltip, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { Tip } from '@components';
import { type SingularKeys, useTrans } from '@libs/i18n';
import {
  ArrowsClockwiseIcon,
  type Icon,
  PencilSimpleIcon,
  ProhibitIcon,
  SealCheckIcon,
  SparkleIcon,
  WarningIcon,
} from '@phosphor-icons/react';

import type { ExtractionReviewField, LocalizedTableColumn } from '../../api/types';
import { fieldHelpText } from '../../organization-profile/components/fields/fieldHelp';
import { ListChipsField } from '../../organization-profile/components/fields/ListChipsField';
import { ReferenceField } from '../../organization-profile/components/fields/ReferenceField';
import { ScalarField } from '../../organization-profile/components/fields/ScalarField';
import { TableGridField } from '../../organization-profile/components/fields/TableGridField';
import { editBoxSx } from '../../organization-profile/utils/tokens';

import { reviewFieldToTemplateField } from './reviewFieldToTemplateField';

interface Props {
  field: ExtractionReviewField;
  value: unknown;
  /** True once the user typed into this field (vs. the untouched AI value). */
  touched: boolean;
  /** True once the user explicitly verified the AI value as-is. */
  verified: boolean;
  disabled: boolean;
  /** True when the user has rejected this field — it's excluded from the save. */
  rejected: boolean;
  /** Validation message (catalog rules) shown beneath the value; blocks save. */
  error?: string;
  /** Span the full grid width (used by wide table fields). */
  fullWidth?: boolean;
  /**
   * Catalog column schema for a `table` field (source of truth), so an empty
   * table still renders its columns. Falls back to the extracted rows when absent.
   */
  tableColumns?: LocalizedTableColumn[] | null;
  /**
   * Catalog description for this field, used as the label tooltip. The
   * extraction payload doesn't carry it, so the review page looks it up in the
   * catalog category; a per-type hint is used when it's absent.
   */
  description?: string | null;
  onChange: (value: unknown) => void;
  onVerify: () => void;
  /** Toggle the field's rejected state. */
  onToggleReject: () => void;
}

/** Field types rendered as a borderless inline text value (matching the design). */
const TEXT_TYPES = new Set(['string', 'email', 'phone', 'number', 'textarea']);
const LIST_TYPES = new Set(['multi_select', 'string_list', 'number_list', 'list']);

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    return false;
  }
}

const valueIsEmpty = (v: unknown): boolean =>
  v == null || v === '' || (Array.isArray(v) && v.length === 0);

type Tone = 'green' | 'blue' | 'amber' | 'violet';

interface ConfidenceBadge {
  labelKey: SingularKeys;
  tone: Tone;
  /** User-action badges (edited/verified) carry an icon; confidence levels show a dot. */
  icon?: Icon;
}

/**
 * Per-tone colors: `fg` drives the pill text + dot, `bg`/`border` fill the pill,
 * and `accent` is the card's thick leading-border stripe.
 */
const TONE: Record<Tone, { fg: string; bg: string; border: string; accent: string }> = {
  green: { fg: 'green.700', bg: 'green.100', border: 'green.300', accent: 'green.500' },
  blue: { fg: 'blue.700', bg: 'blue.100', border: 'blue.300', accent: 'blue.500' },
  amber: { fg: 'yellow.800', bg: 'yellow.200', border: 'yellow.400', accent: 'yellow.700' },
  violet: { fg: 'violet.700', bg: 'violet.100', border: 'violet.300', accent: 'violet.500' },
};

function confidenceBadge(
  field: ExtractionReviewField,
  edited: boolean,
  verified: boolean,
  aiExtracted: boolean,
): ConfidenceBadge | null {
  if (edited)
    return { labelKey: 'app.documentHub.review.field.youEdited', tone: 'blue', icon: PencilSimpleIcon };
  if (verified)
    return {
      labelKey: 'app.documentHub.review.field.verifiedByYou',
      tone: 'green',
      icon: SealCheckIcon,
    };
  if (field.isLowConfidence)
    return { labelKey: 'app.orgProfile.field.lowConfidence', tone: 'amber' };
  // A high/medium AI value the user hasn't touched — tag it so it's clear the
  // value came from AI extraction rather than manual entry.
  if (aiExtracted)
    return { labelKey: 'app.documentHub.review.field.aiExtracted', tone: 'violet', icon: SparkleIcon };
  return null;
}

/**
 * One field in the review screen. Matches the design: a header (label +
 * Required + a text confidence badge), the value shown read-style for text
 * fields (borderless) or via the shared editors for selects/lists/tables, the
 * "you changed it" note, the source citation (p.N chip + quote), and — for an
 * unresolved low-confidence field — an amber warning with a Verify shortcut.
 */
export const ReviewFieldCard: FC<Props> = observer(
  ({
    field,
    value,
    touched,
    verified,
    disabled,
    rejected,
    error,
    fullWidth,
    tableColumns,
    description,
    onChange,
    onVerify,
    onToggleReject,
  }) => {
    const { t } = useTrans();
    const theme = useTheme();

    const templateField = reviewFieldToTemplateField(field, tableColumns);
    const edited = touched && !deepEqual(value, field.aiValue);
    // A rejected field is excluded from the save, so it counts as resolved and
    // never shows the low-confidence "needs review" gate.
    const resolved = touched || verified || rejected;
    const unresolvedLow = field.isLowConfidence && !resolved;
    // The field currently holds a value (AI's or the user's).
    const hasValue = !valueIsEmpty(value);
    // An AI-extracted value the user hasn't edited (nor is it low-confidence).
    const aiExtracted = hasValue && !edited && !field.isLowConfidence;
    const badge = rejected ? null : confidenceBadge(field, edited, verified, aiExtracted);
    // The AI found no value for this field. Surface it so the user knows the
    // blank input is "nothing extracted" rather than an oversight.
    const notExtracted =
      !rejected && !edited && !verified && valueIsEmpty(field.aiValue);
    // Rejected fields are read-only — they're treated as if they don't exist.
    const inputDisabled = disabled || rejected;
    // A validation error only applies to an active (non-rejected) field.
    const showError = !rejected && !!error;

    // The thick leading-border stripe signals the field's state/origin without
    // flooding the whole card: amber = needs verifying, blue = edited manually,
    // green = verified, violet = AI-extracted (untouched). Empty/not-extracted
    // fields (and rejected ones) keep the plain border.
    const accentTone: Tone | null = rejected
      ? null
      : unresolvedLow
        ? 'amber'
        : edited
          ? 'blue'
          : verified
            ? 'green'
            : aiExtracted
              ? 'violet'
              : null;

    const isText = TEXT_TYPES.has(field.type);
    const inputProps = {
      field: templateField,
      value,
      mode: 'edit' as const,
      dirty: edited,
      disabled: inputDisabled,
      onChange,
    };

    let valueNode;
    if (isText) {
      const str = value == null ? '' : String(value);
      const isTextarea = field.type === 'textarea';
      valueNode = (
        <Box
          sx={{
            ...editBoxSx(edited),
            px: 1.5,
            py: isTextarea ? 1.25 : 1.1,
            display: 'flex',
            alignItems: isTextarea ? 'flex-start' : 'center',
            minHeight: isTextarea ? 72 : 'auto',
          }}
        >
          <InputBase
            fullWidth
            multiline={isTextarea}
            minRows={isTextarea ? 3 : undefined}
            // Cap the textarea so long values scroll instead of growing the
            // card to an excessive height.
            maxRows={isTextarea ? 6 : undefined}
            type={field.type === 'number' ? 'number' : 'text'}
            value={str}
            disabled={inputDisabled}
            placeholder={t('app.orgProfile.field.addManually')}
            onChange={(e) =>
              onChange(
                field.type === 'number'
                  ? e.target.value === ''
                    ? null
                    : Number(e.target.value)
                  : e.target.value,
              )
            }
            sx={{
              typography: 'textSM_medium',
              // A typed/extracted value reads as dark text; only the empty-state
              // placeholder stays muted, so "filled" is obvious at a glance.
              color: 'text.primary',
              p: 0,
              '& input::placeholder, & textarea::placeholder': {
                color: 'text.tertiary',
                fontStyle: 'italic',
                opacity: 1,
              },
            }}
          />
        </Box>
      );
    } else if (field.type === 'table') {
      valueNode = <TableGridField {...inputProps} />;
    } else if (field.type === 'reference') {
      valueNode = <ReferenceField {...inputProps} />;
    } else if (LIST_TYPES.has(field.type)) {
      valueNode = <ListChipsField {...inputProps} />;
    } else {
      valueNode = <ScalarField {...inputProps} />;
    }

    return (
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          backgroundColor: rejected
            ? 'grey.100'
            : unresolvedLow
              ? 'yellow.100'
              : notExtracted
                ? 'grey.50'
                : 'background.paper',
          border: 1,
          borderStyle: notExtracted && !accentTone ? 'dashed' : 'solid',
          borderColor: rejected
            ? 'red.200'
            : unresolvedLow
              ? 'yellow.300'
              : 'grey.300',
          ...(accentTone && {
            borderLeftWidth: 4,
            borderLeftColor: TONE[accentTone].accent,
          }),
          ...(rejected && {
            borderLeftWidth: 4,
            borderLeftColor: 'error.main',
          }),
          // A validation error wins the border treatment so the field is easy
          // to spot when the save is blocked.
          ...(showError && {
            borderColor: 'red.300',
            borderLeftWidth: 4,
            borderLeftColor: 'error.main',
          }),
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          // Allow the inner table to scroll instead of stretching the grid cell.
          minWidth: 0,
          ...(fullWidth ? { gridColumn: '1 / -1' } : null),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <Typography
              variant="textSM_bold"
              color={rejected ? 'text.disabled' : 'text.primary'}
              sx={rejected ? { textDecoration: 'line-through' } : undefined}
            >
              {templateField.label}
            </Typography>
            <Tip title={fieldHelpText({ ...field, description }, t)} iconSize={14} />
            {field.isRequired && !rejected && (
              <Typography variant="textXS_regular" color="text.tertiary">
                {t('app.orgProfile.field.required')}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: 'none' }}>
            {rejected ? (
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  typography: 'textXS_semiBold',
                  color: 'error.main',
                  backgroundColor: 'red.100',
                  border: 1,
                  borderColor: 'red.200',
                  borderRadius: 999,
                  px: 0.875,
                  py: 0.25,
                }}
              >
                <ProhibitIcon size={12} weight="fill" />
                {t('app.documentHub.review.field.rejected')}
              </Box>
            ) : badge ? (
              <Box
                component="span"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  typography: 'textXS_semiBold',
                  color: TONE[badge.tone].fg,
                  backgroundColor: TONE[badge.tone].bg,
                  border: 1,
                  borderColor: TONE[badge.tone].border,
                  borderRadius: 999,
                  px: 0.875,
                  py: 0.25,
                }}
              >
                {badge.icon ? (
                  <badge.icon size={12} weight="fill" />
                ) : (
                  <Box
                    component="span"
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: TONE[badge.tone].fg,
                    }}
                  />
                )}
                {t(badge.labelKey)}
              </Box>
            ) : (
              notExtracted && (
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    typography: 'textXS_semiBold',
                    color: 'text.tertiary',
                    backgroundColor: 'grey.100',
                    border: 1,
                    borderColor: 'grey.300',
                    borderRadius: 999,
                    px: 0.875,
                    py: 0.25,
                  }}
                >
                  <ProhibitIcon size={12} />
                  {t('app.documentHub.review.field.notExtracted')}
                </Box>
              )
            )}

            {!disabled &&
              (rejected ? (
                <Button
                  size="small"
                  variant="tertiary"
                  onClick={onToggleReject}
                  sx={{ textTransform: 'none', flex: 'none', minWidth: 0 }}
                >
                  {t('app.documentHub.review.field.restore')}
                </Button>
              ) : (
                // A not-extracted field has no value to reject, so the action
                // is disabled (with an explanatory tooltip).
                <Tooltip
                  title={notExtracted ? t('app.documentHub.review.field.nothingToReject') : ''}
                >
                  <Box component="span" sx={{ flex: 'none' }}>
                    <Button
                      size="small"
                      variant="tertiary"
                      disabled={notExtracted}
                      startIcon={<ProhibitIcon size={14} />}
                      onClick={onToggleReject}
                      sx={{
                        textTransform: 'none',
                        flex: 'none',
                        minWidth: 0,
                        color: 'text.secondary',
                      }}
                    >
                      {t('app.documentHub.review.field.reject')}
                    </Button>
                  </Box>
                </Tooltip>
              ))}
          </Box>
        </Box>

        {rejected ? (
          // Keep the extracted value visible but struck through — the field is
          // excluded from the save, not cleared. Rendered as read-only text
          // because `text-decoration` doesn't apply to native input values.
          <Box
            sx={{
              ...editBoxSx(false),
              px: 1.5,
              py: 1.1,
              display: 'flex',
              alignItems: 'center',
              backgroundColor: 'red.100',
              borderColor: 'red.200',
            }}
          >
            <Typography
              variant="textSM_medium"
              sx={{
                color: 'error.main',
                textDecoration: 'line-through',
                wordBreak: 'break-word',
              }}
            >
              {formatValue(value, t)}
            </Typography>
          </Box>
        ) : (
          valueNode
        )}

        {showError && (
          <Typography variant="textXS_regular" color="error.main">
            {error}
          </Typography>
        )}

        {edited && !rejected && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ArrowsClockwiseIcon size={12} color={theme.palette.blue[700]} />
            <Typography variant="textXS_regular" color="blue.700">
              {t('app.documentHub.review.field.changed', {
                value: formatValue(field.aiValue, t),
              })}
            </Typography>
          </Box>
        )}

        {field.citation?.sourceQuote && (
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
            <Box
              component="span"
              sx={{
                flex: 'none',
                typography: 'textXS_semiBold',
                color: 'text.tertiary',
                backgroundColor: 'grey.200',
                borderRadius: 1,
                px: 0.625,
                py: '1px',
              }}
            >
              {`p.${field.citation.sourcePage ?? '?'}`}
            </Box>
            <Typography
              variant="textXS_regular"
              color="text.tertiary"
              sx={{ fontStyle: 'italic' }}
            >
              {`“${field.citation.sourceQuote}”`}
            </Typography>
          </Box>
        )}
        {field.citation?.citationMissing && (
          <Typography variant="textXS_regular" color="yellow.800">
            {t('app.documentHub.review.field.noCitation')}
          </Typography>
        )}

        {unresolvedLow && !disabled && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              mt: 0.25,
              px: 1.25,
              py: 0.875,
              borderRadius: 1.5,
              backgroundColor: 'yellow.200',
              border: 1,
              borderColor: 'yellow.300',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              <WarningIcon size={14} weight="fill" color={theme.palette.yellow[800]} />
              <Typography variant="textXS_semiBold" color="yellow.800">
                {t('app.documentHub.review.field.needsReview')}
              </Typography>
            </Box>
            <Button
              size="small"
              variant="secondary"
              startIcon={<SealCheckIcon size={14} />}
              onClick={onVerify}
              sx={{ textTransform: 'none', flex: 'none' }}
            >
              {t('app.documentHub.review.field.verify')}
            </Button>
          </Box>
        )}
      </Box>
    );
  },
);

function formatValue(v: unknown, t: ReturnType<typeof useTrans>['t']): string {
  if (v == null || v === '') return t('app.documentHub.review.field.empty');
  if (Array.isArray(v)) {
    if (v.length === 0) return t('app.documentHub.review.field.empty');
    // Table rows (and other object lists) can't be stringified inline — they'd
    // render as "[object Object]". Summarize by row count instead.
    if (v.some((item) => item != null && typeof item === 'object'))
      return t('app.documentHub.review.field.rowsSummary', { count: v.length });
    return v.join(', ');
  }
  if (typeof v === 'object')
    return t('app.documentHub.review.field.rowsSummary', {
      count: Object.keys(v as object).length,
    });
  return String(v);
}
